import { SHStack, SHTypography } from "@components/design-systems";
import { Dollar2SVG, WarningSVG } from "@components/svgs";
import { SelectedInvestmentDTO } from "@models/managed-accounts/entities/step/setup";
import { FeeCalculatorDTO } from "@models/platform-analysis/entities/steps/fee";
import { useTheme } from "@mui/material";
import { defaultTo } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import { AnalysisFeeCalculatorStepConfigs as Configs } from "../../../../../configs";

type Props = {
  selectedInvestments: SelectedInvestmentDTO[];
  selectedFeeCalculators: FeeCalculatorDTO[];
  calculatedTotal: number;

  onValidationChange: (
    isRemainingValid: boolean,
    isInvestmentAllocationValid: boolean,
    isMissingInvestment?: boolean,
    isTemplateRemainingValid?: boolean,
    isInvestmentValid?: boolean,
  ) => void;
};

export const AnalysisFeePortfolioSetupRemainingCalculator = ({
  selectedFeeCalculators,
  selectedInvestments,
  calculatedTotal,
  onValidationChange,
}: Props) => {
  const { palette } = useTheme();

  const totalInvestmentWeight = selectedInvestments.reduce(
    (acc, investment) => {
      const weight = investment.weight ?? 0;
      const isLessThanMinimum = weight < Configs.MinimumInvestmentWeightToSave;
      return acc + (isLessThanMinimum ? 0 : weight);
    },
    0,
  );
  const totalCalculatorWeight = selectedFeeCalculators.reduce(
    (acc, calculator) => {
      const weight = calculator.feeCalculatorData?.weight ?? 0;
      const isLessThanMinimum = weight < Configs.MinimumInvestmentWeightToSave;
      return acc + (isLessThanMinimum ? 0 : weight);
    },
    0,
  );
  const remainingWeight = useMemo(
    () => 100 - (totalInvestmentWeight + totalCalculatorWeight),
    [totalCalculatorWeight, totalInvestmentWeight],
  );

  const totalInvestmentValue = selectedInvestments.reduce((acc, investment) => {
    const value = investment.value;
    const numberValue = !isNaN(Number(value)) ? Number(value) : 0;
    return acc + (numberValue ?? 0);
  }, 0);
  const totalCalculatorValue = selectedFeeCalculators.reduce(
    (acc, calculator) => {
      const value = calculator.feeCalculatorData?.value;
      const numberValue = !isNaN(Number(value)) ? Number(value) : 0;
      return acc + (numberValue ?? 0);
    },
    0,
  );
  const remainingValue = useMemo(
    () => calculatedTotal - (totalInvestmentValue + totalCalculatorValue),
    [calculatedTotal, totalCalculatorValue, totalInvestmentValue],
  );

  const [debouncedAllocationValid, setDebouncedAllocationValid] =
    useState(false);
  const [debouncedRemainingValid, setDebouncedRemainingValid] = useState(false);
  const [debouncedRemainingWeight, setDebouncedRemainingWeight] =
    useState(remainingWeight);
  const [debouncedRemainingValue, setDebouncedRemainingValue] =
    useState(remainingValue);

  const isAllocationValid = useMemo(
    () => totalInvestmentWeight > 0,
    [totalInvestmentWeight],
  );

  const isRemainingValid = useMemo(() => {
    const remainWeightNumber = parseFloat(
      remainingWeight.toFixed(Configs.DefaultDecimalPlaces),
    );

    return (
      (remainWeightNumber === 0 && Math.abs(remainingValue) < 1) ||
      remainingValue === 0 ||
      remainWeightNumber === 0
    );
  }, [remainingWeight, remainingValue]);

  const isTemplateRemainingValid = useMemo(() => {
    const remainWeightNumber = parseFloat(
      remainingWeight.toFixed(Configs.DefaultDecimalPlaces),
    );

    return remainWeightNumber >= 0 && remainWeightNumber <= 100;
  }, [remainingWeight]);

  const isInvestmentWeightValid = useMemo(
    () => totalInvestmentWeight > 0,
    [totalInvestmentWeight],
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAllocationValid(isAllocationValid);
      setDebouncedRemainingValid(isRemainingValid);
      setDebouncedRemainingWeight(remainingWeight);
      setDebouncedRemainingValue(remainingValue);
    }, Configs.MinimumDelayTimeMs);
    return () => clearTimeout(handler);
  }, [isAllocationValid, isRemainingValid, remainingWeight, remainingValue]);

  const isMissingInvestment = useMemo(() => {
    const isMissingFeeCalculator = selectedFeeCalculators.some(
      (calculator) =>
        calculator.isMissingInvestment ||
        (calculator.hasUnavailableInvestmentOption &&
          defaultTo(calculator.feeCalculatorData?.weight, 0) > 0),
    );
    const isMissingInvestment = selectedInvestments.some(
      (investment) =>
        investment.isMissingInvestment ||
        (investment.isUnavailable && defaultTo(investment.weight, 0) > 0),
    );

    return isMissingFeeCalculator || isMissingInvestment;
  }, [selectedInvestments, selectedFeeCalculators]);

  useEffect(() => {
    onValidationChange(
      isRemainingValid,
      isAllocationValid,
      isMissingInvestment,
      isTemplateRemainingValid,
      isInvestmentWeightValid,
    );
  }, [
    isRemainingValid,
    isAllocationValid,
    onValidationChange,
    isMissingInvestment,
    isTemplateRemainingValid,
    isInvestmentWeightValid,
  ]);

  const fixedDecimalsWeight = useMemo(
    () =>
      Number(debouncedRemainingWeight.toFixed(Configs.DefaultDecimalPlaces)),
    [debouncedRemainingWeight],
  );
  const showDecimalPlacesForNumberValue = useMemo(
    () => Math.abs(fixedDecimalsWeight) % 1 !== 0,
    [fixedDecimalsWeight],
  );

  const fixedDecimalsValue = useMemo(
    () => Number(debouncedRemainingValue.toFixed(Configs.DefaultDecimalPlaces)),
    [debouncedRemainingValue],
  );
  const showDecimalPlacesForValue = useMemo(
    () => Math.abs(fixedDecimalsValue) % 1 !== 0,
    [fixedDecimalsValue],
  );

  return (
    <SHStack
      width={"100%"}
      alignItems={"end"}
      direction={"column"}
      marginLeft={1}
      paddingRight={1}
    >
      <SHStack
        width={550}
        height={40}
        direction={"row"}
        alignItems={"end"}
        justifyContent={"end"}
        padding={1}
        bgcolor={palette.secondary["50"]}
        border={`1px solid ${palette.secondary["100"]}`}
      >
        <SHStack
          alignItems={"start"}
          justifyContent={"center"}
          height={"100%"}
          width={210}
          paddingLeft={1.5}
        >
          <SHTypography variant="subtitle2" fontSize="18px">
            Remaining
          </SHTypography>
        </SHStack>
        <SHStack
          direction={"row"}
          justifyContent={"end"}
          alignItems={"center"}
          width={115}
          marginRight={2}
          paddingRight={1.5}
          height={"100%"}
          gap={2}
        >
          <SHTypography variant="subtitle2" fontSize="18px">
            <NumericFormat
              displayType="text"
              defaultValue={100}
              value={debouncedRemainingWeight}
              thousandSeparator=","
              decimalScale={
                showDecimalPlacesForNumberValue
                  ? Configs.DefaultDecimalPlaces
                  : 0
              }
              fixedDecimalScale={showDecimalPlacesForNumberValue}
              allowNegative={
                debouncedRemainingWeight.toFixed(
                  Configs.DefaultDecimalPlaces,
                ) !== Configs.NegativeZeroString
              }
            />
          </SHTypography>

          <SHTypography
            variant="subtitle1"
            fontSize={"18px"}
            fontWeight={900}
            lineHeight={"160%"}
            marginRight={"3px"}
            disabled
          >
            %
          </SHTypography>
        </SHStack>

        <SHStack
          direction={"row"}
          justifyContent={"start"}
          alignItems={"center"}
          width={215}
          height={"100%"}
          paddingLeft={0}
          gap={2}
          sx={{
            svg: {
              marginBottom: "3px",
            },
          }}
        >
          <Dollar2SVG width={18} height={18} />
          <SHTypography variant="subtitle2" fontSize="18px">
            <NumericFormat
              displayType="text"
              defaultValue={0}
              value={debouncedRemainingValue}
              thousandSeparator=","
              decimalScale={
                showDecimalPlacesForValue ? Configs.DefaultDecimalPlaces : 0
              }
              fixedDecimalScale={showDecimalPlacesForValue}
              allowNegative={
                debouncedRemainingValue.toFixed(
                  Configs.DefaultDecimalPlaces,
                ) !== Configs.NegativeZeroString
              }
            />
          </SHTypography>
        </SHStack>
      </SHStack>

      <SHStack
        width={550}
        height={25}
        direction={"row"}
        alignItems={"center"}
        alignContent={"center"}
        justifyContent={"center"}
        justifyItems={"center"}
      >
        {(!debouncedAllocationValid || !debouncedRemainingValid) && (
          <SHStack direction={"row"} gap={0.5} marginTop={0.5}>
            <WarningSVG
              width={13}
              height={13}
              backgroundColor={palette.error.main}
              iconColor={palette.common.white}
              style={{ marginTop: 2 }}
            />
            <SHTypography
              color={palette.error.main}
              variant="body2"
              fontSize={13}
              fontWeight={600}
              height={25}
              paddingTop={"1px !important"}
            >
              {[
                !debouncedAllocationValid &&
                  "At least one model must have a weight.",
                !debouncedRemainingValid &&
                  "Remaining allocation must equal zero.",
              ]
                .filter(Boolean)
                .join(" ")}
            </SHTypography>
          </SHStack>
        )}
      </SHStack>
    </SHStack>
  );
};
